import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Scroll3DSection, Tilt3D } from './Scroll3D';
import {
  Globe,
  Smartphone,
  Cloud,
  Brain,
  Shield,
  TrendingUp,
  Code,
  Database,
  Cpu,
  Zap,
  Lock,
  BarChart3,
  Wifi,
  Star,
  ArrowRight,
  Glasses
} from 'lucide-react';

const Services = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const serviceIcons = [
    { icon: Code, color: 'from-neon-green to-neon-cyan' },
    { icon: Smartphone, color: 'from-neon-cyan to-neon-blue' },
    { icon: Globe, color: 'from-neon-blue to-neon-purple' },
    { icon: Shield, color: 'from-neon-purple to-neon-green' },
    { icon: Database, color: 'from-neon-green to-neon-cyan' },
    { icon: Cloud, color: 'from-neon-cyan to-neon-blue' },
    { icon: Glasses, color: 'from-neon-blue to-neon-purple' },
  ];

  const services = [
    {
      title: t('services.web.title'),
      description: t('services.web.description'),
      features: [
        t('services.web.features.0'),
        t('services.web.features.1'),
        t('services.web.features.2'),
        t('services.web.features.3'),
      ],
    },
    {
      title: t('services.mobile.title'),
      description: t('services.mobile.description'),
      features: [
        t('services.mobile.features.0'),
        t('services.mobile.features.1'),
        t('services.mobile.features.2'),
        t('services.mobile.features.3'),
      ],
    },
    {
      title: t('services.cloud.title'),
      description: t('services.cloud.description'),
      features: [
        t('services.cloud.features.0'),
        t('services.cloud.features.1'),
        t('services.cloud.features.2'),
        t('services.cloud.features.3'),
      ],
    },
    {
      title: t('services.security.title'),
      description: t('services.security.description'),
      features: [
        t('services.security.features.0'),
        t('services.security.features.1'),
        t('services.security.features.2'),
        t('services.security.features.3'),
      ],
    },
    {
      title: t('services.ai.title'),
      description: t('services.ai.description'),
      features: [
        t('services.ai.features.0'),
        t('services.ai.features.1'),
        t('services.ai.features.2'),
        t('services.ai.features.3'),
      ],
    },
    {
      title: t('services.vr.title'),
      description: t('services.vr.description'),
      features: [
        t('services.vr.features.0'),
        t('services.vr.features.1'),
        t('services.vr.features.2'),
        t('services.vr.features.3'),
      ],
    },
  ];

  return (
    <section id="services" ref={ref} className="py-10 bg-transparent dark:bg-transparent relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-40 h-40 bg-gradient-to-r from-neon-green/15 to-neon-cyan/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-gradient-to-r from-neon-blue/15 to-neon-purple/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-gradient-to-r from-neon-green/8 to-neon-purple/8 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-gradient-to-r from-neon-cyan/10 to-neon-blue/10 rounded-full blur-2xl"></div>
      </div>

      <Scroll3DSection className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Enhanced Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center space-x-3 rtl:space-x-reverse bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 backdrop-blur-xl border border-neon-green/40 rounded-full px-8 py-4 mb-8 shadow-2xl shadow-neon-green/20"
          >
            <Zap className="w-6 h-6 text-neon-green" />
            <span className="text-neon-green font-bold text-lg">{t('services.badge')}</span>
            <Star className="w-5 h-5 text-neon-cyan" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-8"
          >
            <span className="gradient-text-neon drop-shadow-2xl">{t('services.title')}</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed font-light"
          >
            {t('services.subtitle')}
          </motion.p>
        </motion.div>

        {/* Enhanced Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="w-full h-full"
            >
              <Tilt3D className="w-full h-full">
                <div className="group relative bg-black/50 backdrop-blur-xl border border-neon-green/30 rounded-3xl p-10 hover:border-neon-green/50 transition-all duration-500 card-hover-neon shadow-2xl shadow-neon-green/10 flex flex-col h-full">
                  {/* Enhanced Service Icon */}
                  <div className={`relative w-24 h-24 bg-gradient-to-r ${serviceIcons[index].color} rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-2xl`}>
                    {React.createElement(serviceIcons[index].icon, { className: "w-12 h-12 text-black" })}
                    <div className="absolute -inset-2 bg-gradient-to-r from-neon-green to-neon-cyan rounded-3xl blur opacity-40 group-hover:opacity-60 transition-opacity duration-300"></div>
                  </div>

                  {/* Enhanced Service Content */}
                  <h3 className="text-3xl font-black text-white mb-6 group-hover:text-neon-green transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-white/80 text-lg mb-6 leading-relaxed min-h-[60px]">
                    {service.description}
                  </p>
                  <ul className="mb-8 grid grid-cols-1 gap-3">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center space-x-3 rtl:space-x-reverse">
                        <span className="w-2 h-2 bg-neon-green rounded-full mr-2"></span>
                        <span className="text-white/80 text-base font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Tilt3D>
            </motion.div>
          ))}
        </motion.div>

        {/* Enhanced Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="text-center mt-20"
        >
          <motion.button
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.95 }}
            className="group relative bg-gradient-to-r from-neon-green to-neon-cyan text-black px-16 py-6 rounded-3xl font-black text-xl flex items-center space-x-4 rtl:space-x-reverse mx-auto hover:shadow-2xl hover:shadow-neon-green/30 transition-all duration-300 overflow-hidden"
          >
            <span>{t('services.cta')}</span>
            <Cpu className="w-7 h-7 group-hover:rotate-12 transition-transform duration-300" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          </motion.button>
        </motion.div>

        {/* Additional Features Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-10"
        >
          {[
            {
              title: t('services.additionalFeatures.support.title'),
              description: t('services.additionalFeatures.support.description'),
              icon: Shield,
              color: 'from-neon-green to-neon-cyan',
            },
            {
              title: t('services.additionalFeatures.scalable.title'),
              description: t('services.additionalFeatures.scalable.description'),
              icon: TrendingUp,
              color: 'from-neon-cyan to-neon-blue',
            },
            {
              title: t('services.additionalFeatures.security.title'),
              description: t('services.additionalFeatures.security.description'),
              icon: Lock,
              color: 'from-neon-blue to-neon-purple',
            },
          ].map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1 + index * 0.2 }}
              className="text-center group"
            >
              <div className={`w-20 h-20 bg-gradient-to-r ${feature.color} rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-2xl`}>
                <feature.icon className="w-10 h-10 text-black" />
              </div>
              <h4 className="text-2xl font-black text-white mb-4 group-hover:text-neon-green transition-colors duration-300">
                {feature.title}
              </h4>
              <p className="text-white/70 leading-relaxed text-lg">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </Scroll3DSection>
    </section>
  );
};

export default Services; 